import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  EmailProvider,
  useEmailConnectionsService,
  EMAIL_CONNECTIONS_UPDATED, ValidationError
} from 'nekst-api';
import { useEventsPublisher } from 'event-bus';

export type EmailOAuthCallbackStatus = 'processing' | 'success' | 'error';

export interface EmailOAuthCallbackState {
  status: EmailOAuthCallbackStatus;
  errorMessage: string;
  provider: EmailProvider | null;
}

export function useEmailOAuthCallback() {
  const [searchParams] = useSearchParams();
  const { provider: providerParam } = useParams<{ provider: string }>();
  const navigate = useNavigate();

  // Use refs to prevent unnecessary re-renders and keep services stable
  const emailConnectionsServiceRef = useRef(useEmailConnectionsService());
  const eventsPublisherRef = useRef(useEventsPublisher());

  // Track processed codes to prevent duplicate processing
  const processedCodesRef = useRef(new Set<string>());

  const [state, setState] = useState<EmailOAuthCallbackState>({
    status: 'processing',
    errorMessage: '',
    provider: null
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Convert provider parameter to enum
  const getProviderType = (provider: string): EmailProvider | null => {
    switch (provider?.toLowerCase()) {
      case 'gmail':
        return EmailProvider.GMAIL;
      default:
        return null;
    }
  };

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof ValidationError) {
      return error.errors['email'].join('');
    } else if (error instanceof Error) {
      return error.message;
    }
    return 'Failed to connect your email account. Please try again.';
  };

  useEffect(() => {
    const processEmailOAuthCallback = async () => {
      try {
        // Prevent concurrent processing
        if (isProcessing) {
          return;
        }

        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const provider = getProviderType(providerParam || '');

        if (!provider) {
          setState({
            status: 'error',
            errorMessage: 'Invalid email provider specified.',
            provider: null
          });
          return;
        }

        setState(prev => ({ ...prev, provider }));

        if (error) {
          setState(prev => ({
            ...prev,
            status: 'error',
            errorMessage: 'Email authorization was cancelled or failed.'
          }));
          return;
        }

        if (!code) {
          setState(prev => ({
            ...prev,
            status: 'error',
            errorMessage: 'No authorization code received from email provider.'
          }));
          return;
        }

        // Check if we've already processed this code
        if (processedCodesRef.current.has(code)) {
          return;
        }

        // Mark this code as being processed
        processedCodesRef.current.add(code);
        setIsProcessing(true);

        // Process the email connection creation
        await emailConnectionsServiceRef.current.createConnection(provider, { code });

        setState(prev => ({
          ...prev,
          status: 'success',
          errorMessage: ''
        }));

        // Publish email connections updated event to refresh the widget
        setTimeout(() => {
          eventsPublisherRef.current.publish(EMAIL_CONNECTIONS_UPDATED, {});
        }, 500);

        // Redirect to email settings page after a brief success message
        setTimeout(() => {
          navigate('/settings/email');
        }, 1500);

      } catch (error) {
        console.error('Email OAuth callback error:', error);

        setState(prev => ({
          ...prev,
          status: 'error',
          errorMessage: getErrorMessage(error)
        }));
      } finally {
        setIsProcessing(false);
      }
    };

    processEmailOAuthCallback();
  }, []);

  const handleRetry = () => {
    navigate('/settings/email');
  };

  const handleReturnToSettings = () => {
    navigate('/settings/email');
  };

  return {
    ...state,
    handleRetry,
    handleReturnToSettings
  };
}
